import { useEffect } from 'react';

/**
 * SimpleModeBootstrap — silent detector that flips `<html>` into
 * `vaad-simple-mode` at page load when the viewport is TINY (Cat S22,
 * Quin flip, KaiOS phones — all ~240–320 px wide) AND the user hasn't
 * ever manually toggled Simple mode.
 *
 * Feb-2026 (user): "i wont want everyone to see ill want it to be in
 * settings only" — the always-visible bottom-right chip was removed.
 * Simple mode is now:
 *   • Auto-enabled once on tiny viewports (invisible convenience)
 *   • Otherwise controlled only from Settings → "Simple mode
 *     (Cat / Quin / feature phones)".
 *
 * Renders NOTHING to the DOM — just runs the detection effect on mount.
 */

const STORAGE_KEY = 'vaad_settings_v1';
const AUTO_KEY = 'vaad_simple_mode_auto_v1';
const TINY_WIDTH_PX = 340;

const readSetting = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { simpleMode: false, touched: false };
    const parsed = JSON.parse(raw);
    return {
      simpleMode: Boolean(parsed.simpleMode),
      touched: parsed._simpleModeTouched === true,
    };
  } catch {
    return { simpleMode: false, touched: false };
  }
};

const writeSetting = (patch) => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...parsed, ...patch }));
  } catch { /* localStorage denied — silently ignore */ }
};

const applyClass = (on) => {
  document.documentElement.classList.toggle('vaad-simple-mode', on);
};

const SimpleModeBootstrap = () => {
  // Auto-detect on first ever load: if viewport is tiny AND user has never
  // touched the setting AND we haven't auto-flipped before this session.
  useEffect(() => {
    const { touched, simpleMode } = readSetting();
    // Always mirror the stored setting on <html> so a fresh tab picks up
    // the user's previous choice before SettingsPage's own effect fires.
    if (simpleMode) applyClass(true);
    const alreadyAuto = sessionStorage.getItem(AUTO_KEY) === '1';
    if (touched || alreadyAuto) return;
    if (window.innerWidth <= TINY_WIDTH_PX) {
      sessionStorage.setItem(AUTO_KEY, '1');
      writeSetting({ simpleMode: true });
      applyClass(true);
    }
  }, []);

  return null;
};

export default SimpleModeBootstrap;

